import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
// @mui
import { LoadingButton } from "@mui/lab";
import { Container, Grid, Card, Stack, Typography } from "@mui/material";
// components
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import FormProvider, { RHFTextField } from "../components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    addToBetaGroup,
    isUserInBetaGroup,
    removeFromBetaGroup,
    generateBetaInviteLink,
} from "../controllers/user";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
    IconButton,
    InputAdornment,
    MenuItem,
    TextField,
    Divider,
} from "@mui/material";

const EXPIRY_OPTIONS = [
    { label: "1 hour", value: 3600 },
    { label: "24 hours", value: 86400 },
    { label: "7 days", value: 604800 },
    { label: "30 days", value: 2592000 },
];

// ----------------------------------------------------------------------

export default function BetaGroup() {
    const { themeStretch } = useSettingsContext();
    const { enqueueSnackbar } = useSnackbar();

    const defaultValues = {
        userId: "",
    };

    const schema = Yup.object().shape({
        userId: Yup.string().required("User ID is required"),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const {
        reset,
        handleSubmit,
        formState: { isSubmitting, isValid },
    } = methods;

    const [isInBetaGroup, setIsInBetaGroup] = useState<boolean | null>(null);
    const [isCheckingBetaStatus, setIsCheckingBetaStatus] = useState(false);

    // Invite link state
    const [selectedExpiry, setSelectedExpiry] = useState<number>(604800); // Default 7 days
    const [generatedLink, setGeneratedLink] = useState<string>("");
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);

    const handleGenerateInviteLink = async () => {
        setIsGeneratingLink(true);
        try {
            const result = await generateBetaInviteLink(selectedExpiry);
            setGeneratedLink(result.inviteUrl);
            enqueueSnackbar("Invite link generated successfully");
        } catch (error) {
            console.error("Error generating invite link:", error);
            enqueueSnackbar("Failed to generate invite link", { variant: "error" });
        } finally {
            setIsGeneratingLink(false);
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(generatedLink);
        enqueueSnackbar("Link copied to clipboard");
    };

    const debounceCheckUserBetaGroup = useDebouncedCallback(
        async (userId?: string) => {
            if (!userId || userId.length === 0) {
                setIsInBetaGroup(null);
                setIsCheckingBetaStatus(false);
                return;
            }

            setIsCheckingBetaStatus(true);
            try {
                const isInBetaGroup = await isUserInBetaGroup(userId);
                setIsInBetaGroup(isInBetaGroup);
            } catch (error) {
                console.error("Error checking beta status:", error);
                setIsInBetaGroup(null);
            } finally {
                setIsCheckingBetaStatus(false);
            }
        },
        300
    );

    useEffect(() => {
        const subscription = methods.watch((value) => {
            debounceCheckUserBetaGroup(value.userId);
        });
        return () => subscription.unsubscribe();
    }, [methods, debounceCheckUserBetaGroup]);

    const onSubmit = async (data: { userId: string }) => {
        // Determine action based on current beta status
        const action = isInBetaGroup ? "remove" : "add";
        
        if (action === "add") {
            addToBetaGroup(data.userId)
                .then(() => {
                    reset();
                    setIsInBetaGroup(null); // Reset status after successful action
                    enqueueSnackbar(
                        `Successfully added user to beta group: ${data.userId}`
                    );
                })
                .catch((error) => {
                    console.error(error);
                    enqueueSnackbar(
                        `Could not add user to beta group: ${data.userId}`,
                        {
                            variant: "error",
                        }
                    );
                });
        } else if (action === "remove") {
            removeFromBetaGroup(data.userId)
                .then(() => {
                    reset();
                    setIsInBetaGroup(null); // Reset status after successful action
                    enqueueSnackbar(
                        `Successfully removed user from beta group: ${data.userId}`
                    );
                })
                .catch((error) => {
                    console.error(error);
                    enqueueSnackbar(
                        `Could not remove user from beta group: ${data.userId}`,
                        {
                            variant: "error",
                        }
                    );
                });
        }
    };

    return (
        <>
            <Helmet>
                <title> Beta Group | Admin Portal</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : "xl"}>
                <Typography variant="h3" component="h1" paragraph>
                    Beta Group
                </Typography>

                <FormProvider
                    methods={methods}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Card sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <RHFTextField
                                        name="userId"
                                        label="User ID"
                                        required
                                    />
                                </Stack>
                                <Stack sx={{ mt: 2 }}>
                                    {isCheckingBetaStatus && (
                                        <Typography
                                            variant="subtitle2"
                                            color={"text.secondary"}
                                        >
                                            Checking beta status...
                                        </Typography>
                                    )}
                                    {!isCheckingBetaStatus && isInBetaGroup === true && (
                                        <Typography
                                            variant="subtitle2"
                                            color={"success.main"}
                                        >
                                            ✓ This user is in the beta group.
                                        </Typography>
                                    )}
                                    {!isCheckingBetaStatus && isInBetaGroup === false && (
                                        <Typography
                                            variant="subtitle2"
                                            color={"text.secondary"}
                                        >
                                            ✗ This user is not in the beta group.
                                        </Typography>
                                    )}
                                </Stack>
                            </Card>
                            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                                {!isCheckingBetaStatus && isInBetaGroup === true ? (
                                    <LoadingButton
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="error"
                                        size="large"
                                        loading={isSubmitting}
                                        disabled={!isValid}
                                    >
                                        Remove From Beta Group
                                    </LoadingButton>
                                ) : !isCheckingBetaStatus && isInBetaGroup === false ? (
                                    <LoadingButton
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="success"
                                        size="large"
                                        loading={isSubmitting}
                                        disabled={!isValid}
                                    >
                                        Add To Beta Group
                                    </LoadingButton>
                                ) : (
                                    <LoadingButton
                                        fullWidth
                                        type="submit"
                                        variant="outlined"
                                        size="large"
                                        loading={isCheckingBetaStatus}
                                        disabled={true}
                                    >
                                        {isCheckingBetaStatus
                                            ? "Checking Beta Status..."
                                            : "Enter a User ID to Continue"}
                                    </LoadingButton>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </FormProvider>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h5" component="h2" paragraph>
                    Generate Invite Link
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create a shareable link that allows users to join the beta program.
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ p: 3 }}>
                            <Stack spacing={3}>
                                <TextField
                                    select
                                    label="Link Expiry"
                                    value={selectedExpiry}
                                    onChange={(e) => setSelectedExpiry(Number(e.target.value))}
                                    fullWidth
                                >
                                    {EXPIRY_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {generatedLink && (
                                    <TextField
                                        label="Invite Link"
                                        value={generatedLink}
                                        fullWidth
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={handleCopyLink}
                                                        edge="end"
                                                        title="Copy to clipboard"
                                                    >
                                                        <ContentCopyIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            </Stack>
                        </Card>
                        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                            <LoadingButton
                                fullWidth
                                variant="contained"
                                size="large"
                                loading={isGeneratingLink}
                                onClick={handleGenerateInviteLink}
                            >
                                Generate Invite Link
                            </LoadingButton>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
